import { Alert, Button, Card, Divider, Space, Steps, notification } from "antd";
import { useEffect, useState } from "react";
import { getPipelinesService, getProjectService } from "../../backend";
import { DeploymentState, TemplateType, PipelineSpec, FormState, EnvironmentDetails } from "../../types";
import PipelineStatusTag from "../PipelineStatusTag";
import yaml from "js-yaml";
import { useNavigate } from "react-router-dom";
import TemplatePipelineStatusDrawer from "./TemplatePipelineStatusDrawer";
import { CloseCircleOutlined, LoadingOutlined } from "@ant-design/icons";
import { useRecoilState, useRecoilValue } from "recoil";
import { currentProjectState } from "../../recoil/project";
import { currentTemplateStepState, templateState } from "../../recoil/template";
import { useCardClassName } from "../../contexts/Helpers/helpers";
import { useCurrentProject } from "../../contexts/Project";
import DeployTemplateSummary, { DeployTemplateSummaryProps } from "./DeployTemplateSummaryModule";
import { isEnvironmentTemplate } from "../../utils/check-environment-template";
import { PipelineBuildDetailsStagesStatusEnum } from "@microtica/ms-ap-sdk";

const stepStatus: { [status: string]: "wait" | "process" | "finish" | "error" } = {
    PENDING: "wait",
    RUNNING: "process",
    SUCCEEDED: "finish",
    DONE: "finish",
    FAILED: "error"
}
interface DeployTemplateModuleProps {
    templateType: TemplateType;
    env?: EnvironmentDetails;
    templateExecutionId?: string;
    buildId?: string;
    appName?: string;
    creatingCluster: boolean;
    clusterAppName?: string;
    onDone?: () => void
    onDeploy: () => void;
    disabled?: boolean;
    addBackButton?: boolean;
    summary?: DeployTemplateSummaryProps;
}

const DeployTemplate = ({
    templateType,
    env,
    templateExecutionId,
    buildId,
    appName,
    creatingCluster,
    clusterAppName,
    onDone,
    onDeploy,
    disabled,
    addBackButton,
    summary
}: DeployTemplateModuleProps) => {
    const navigate = useNavigate();
    const currentProject = useRecoilValue(currentProjectState);
    const { updateCurrentProject, shouldInitiateTrial } = useCurrentProject();
    const [currentStep, setCurrentStep] = useState<{ step: number, status: string }>({ step: -1, status: "QUEUED" });
    const [visibleLogs, setVisibleLogs] = useState(false);
    const [selectedPipeline, setSelectedPipeline] = useState<{ id: string; name: string; buildId: string; errorMessage?: string; }>();
    const [environmentTemplateDone, setEnvironmentTemplateDone] = useState<boolean>(false);
    const [currentTemplateState, setCurrentTemplateState] = useRecoilState(templateState);
    const currentTemplateStep = useRecoilValue(currentTemplateStepState);
    const [deploymentState, setDeploymentState] = useState<DeploymentState>({
        stages: [],
        status: "QUEUED"
    });
    const [deploymentPlaceholder, setDeploymentPlaceholder] = useState<any>(
        <div>
            Start the deployment for your {templateType === "environment" ? "environment" : "application"}
        </div>
    );


    const cardClassName = useCardClassName(
        !!disabled,
        [disabled, currentTemplateStep]
    );

    useEffect(() => {
        if (templateExecutionId && buildId) {
            loadData();
            const interval = setInterval(async () => {
                if (templateExecutionId && buildId) {
                    const status = await loadData();
                    if (["SUCCEEDED", "FAILED"].includes(status)) {
                        clearInterval(interval);
                    }
                }
            }, 5000);
            return () => clearInterval(interval);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [templateExecutionId, buildId]);

    useEffect(() => {
        setDeploymentPlaceholder(disabled ?
            <div>Start the deployment for your {templateType === "environment" ? "environment" : "application"}</div> :
            <></>
        );
        handleSetTemplateState(disabled ? "disabled" : "editing");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [disabled]);

    useEffect(() => {
        if (environmentTemplateDone) {
            onDone?.();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [environmentTemplateDone]);

    const handleSetTemplateState = (mode: FormState) => {
        setCurrentTemplateState({
            ...currentTemplateState,
            deploy: mode
        });
    }

    const handleShowLogs = async (pipelineName: string, pipelineId: string) => {
        const { data: { latestBuild } } = await getPipelinesService()
            .getPipelineBuilds(currentProject!.id, pipelineId)
            .then(res => {
                if (res.data.builds.length === 0) {
                    throw new Error("No builds");
                } else {
                    return res;
                }
            })
            .catch(async _ => {
                return getPipelinesService().getPipelineBuilds(currentProject!.id, templateExecutionId!);
            });

        setSelectedPipeline({
            id: latestBuild?.pipelineId!,
            name: pipelineName,
            buildId: latestBuild?.id!,
            errorMessage: latestBuild?.errorMessage
        });
        setVisibleLogs(true);
    }

    const loadData = async () => {
        const { data: templateExecution } = await getPipelinesService().getPipelineBuild(
            currentProject!.id,
            templateExecutionId!,
            buildId!
        ).catch(err => {
            // Ignore error 404 because it will always happen (only once)
            if (err.response.data.code !== 404) {
                notification.error({
                    message: "An error occured",
                    description: err.response.data.message
                });
            }
            throw err;
        })

        const pipelineSpec = yaml.load(templateExecution.microticaYaml) as PipelineSpec;

        // if execution status transitions, remove the placeholder text
        if (templateExecution.status !== "QUEUED") {
            setDeploymentPlaceholder(<></>)
        }

        const newDeploymentState = {
            loading: ["QUEUED", "PENDING", "RUNNING"].includes(templateExecution.status),
            status: templateExecution.status,
            stages: (templateExecution.stages || []).map(stage => ({
                name: stage.name,
                current: stage.status === "RUNNING" || stage.status === "FAILED",
                status: stage.status,
                result:
                    <>
                        {
                            templateExecution.steps?.filter(step => step.stage === stage.name)
                                .map(step => {
                                    const { type, pipeline } = pipelineSpec.steps[step.name];
                                    return (
                                        <div className="flex-justify-space-between" key={step.name}>
                                            <Space>
                                                <PipelineStatusTag status={step.status} />
                                                <div>{step.name}</div>
                                            </Space>
                                            {
                                                type === "trigger-pipeline" && pipeline &&
                                                <Button
                                                    type="link"
                                                    style={{ paddingRight: 0 }}
                                                    onClick={() => {
                                                        handleShowLogs(step.name, pipeline)
                                                    }}
                                                >
                                                    View Logs
                                                </Button>
                                            }
                                        </div>
                                    );
                                })
                        }
                    </>
            })),
            error: templateExecution.errorMessage
        };

        if (
            templateType === "environment" &&
            templateExecution.status === "SUCCEEDED"
        ) {
            newDeploymentState.stages.push({
                name: "Done",
                current: true,
                status: PipelineBuildDetailsStagesStatusEnum.SUCCEEDED,
                result:
                    <div className="flex-justify-space-between">
                        <Space>
                            <div>Your environment is being deployed</div>
                        </Space>
                        {
                            <a href={`/projects/${currentProject.id}/environments/${env?.id}/deployments`} target="_blank" rel="noreferrer">
                                <Button
                                    type="link"
                                    style={{ paddingRight: 0 }}
                                >
                                    View deployment
                                </Button>
                            </a>
                        }
                    </div>
            });
            setEnvironmentTemplateDone(true);
        } else if (
            templateType === "cluster" &&
            templateExecution.status === "SUCCEEDED"
        ) {
            const serviceName = pipelineSpec.steps[appName!].service;
            newDeploymentState.stages.push({
                name: "Done",
                current: true,
                status: PipelineBuildDetailsStagesStatusEnum.SUCCEEDED,
                result:
                    <div className="flex-justify-space-between">
                        <Space>
                            <div>Your application has been deployed</div>
                        </Space>
                        {
                            <Button
                                type="link"
                                style={{ paddingRight: 0 }}
                                onClick={() => !creatingCluster ?
                                    navigate(`/projects/${currentProject.id}/environments/${env?.id}/apps/${env?.clusterId}/${env?.clusterNamespace}/${serviceName}`) :
                                    navigate(`/projects/${currentProject.id}/environments/${env?.id}/apps/${env?.id}-${clusterAppName}/microtica/${serviceName}`)
                                }
                            >
                                View application
                            </Button>
                        }
                    </div>
            });
        }
        setDeploymentState(newDeploymentState);

        const currentStageIndex = newDeploymentState.stages.findIndex(stage => stage.current);
        setCurrentStep(currentStageIndex !== -1 ? { step: currentStageIndex, status: newDeploymentState.stages[currentStageIndex].status } : { step: -1, status: "QUEUED" });


        return templateExecution.status;
    }

    const handleActiveStepChange = () => {
        const step = templateType === "environment" ? "env" : "cluster";
        setCurrentTemplateState(currentTemplateState => ({
            ...currentTemplateState,
            [step]: "editing"
        }));
    }

    const handleDeploy = async () => {
        // reset the deployment state to the initial values
        setDeploymentState({
            stages: [],
            status: "QUEUED",
            loading: true
        });
        setDeploymentPlaceholder(
            <Space>
                <LoadingOutlined spin />
                <div>Preparing your {templateType === "environment" ? "environment" : "application"} for deployment...</div>
            </Space>
        );


        if (isEnvironmentTemplate(creatingCluster, templateType) && shouldInitiateTrial) {
            const { data: { paymentPlans } } = await getProjectService().getAllPaymentPlans();
            const starterPlan = paymentPlans.find(plan => plan.id === "STARTER")!;
            await getProjectService().changeProjectSubscription(
                currentProject!.id,
                {
                    paymentPlanId: starterPlan.id!,
                    priceId: starterPlan.pricing.find(p => p.isDefault)!.id
                }
            );
            await updateCurrentProject(currentProject.id);
        }
        onDeploy();
    }


    return (
        <Card bordered className={cardClassName}>
            <Card.Meta title="Deploy" />
            <Divider />
            {
                summary &&
                <DeployTemplateSummary
                    templateType={summary?.templateType}
                    gitAccount={summary!.gitAccount}
                    environment={summary!.environment}
                    appName={summary!.appName}
                    framework={summary!.framework}
                    templateRepositoryUrl={summary!.templateRepositoryUrl}
                    visible={!disabled && !deploymentState.stages?.length}
                    shouldCreateCluster={summary!.shouldCreateCluster}
                    clusterAppName={summary!.clusterAppName}
                    footerMessage={deploymentPlaceholder}
                />
            }
            <Steps
                direction="vertical"
                size="small"
                current={currentStep.step}
                status={stepStatus[currentStep.status]}
                items={
                    deploymentState.stages?.map(step => ({  
                        title: step.name,
                        description: step.result
                    }))
                }
            />
            {
                deploymentState.error &&
                <>
                    <br />
                    <Alert
                        message={
                            <>
                                <CloseCircleOutlined style={{ color: "red" }} /> {deploymentState.error}
                            </>
                        }
                        type="error"
                    />
                </>
            }
            {
                isEnvironmentTemplate(creatingCluster, templateType) && shouldInitiateTrial ?
                    <span>
                        <br />
                        Continuing will initiate a 7 day trial of the <a href="https://microtica.com/pricing" target="_blank" rel="noreferrer">Starter plan</a>.
                    </span> : null
            }
            <Divider />
            <div className={!!addBackButton ? "flex-justify-space-between" : "flex-justify-space-between flex-align-center"}>
                {!!addBackButton ?
                    <Button
                        disabled={deploymentState?.loading || ["SUCCEEDED", "FAILED"].includes(deploymentState.status)}
                        onClick={handleActiveStepChange}>
                        Back
                    </Button> : null
                }
                <Button
                    type="primary"
                    htmlType="submit"
                    onClick={handleDeploy}
                    loading={deploymentState?.loading}
                    disabled={disabled || deploymentState?.loading || ["SUCCEEDED", "FAILED"].includes(deploymentState.status)}
                >
                    {
                        deploymentState?.loading ? "Deploying" : "Deploy"
                    }
                </Button>
            </div>
            <TemplatePipelineStatusDrawer
                title={selectedPipeline?.name || "Logs"}
                footer="Template deployment logs"
                visible={visibleLogs}
                onClose={() => setVisibleLogs(false)}
                buildId={selectedPipeline?.buildId}
                templateExecutionId={selectedPipeline?.id}
                errorMessage={selectedPipeline?.errorMessage}
            />
        </Card>
    );
}

export default DeployTemplate;